import { Link } from '@tanstack/react-router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import type { NavItem } from './types';

// On the dark-teal rail: active = softened turquoise pill (lower contrast
// against the teal); inactive = light icon with a turquoise hover wash.
const iconBgActive =
  'bg-brand-accent/30 text-sidebar-foreground ring-1 ring-inset ring-brand-accent/40';
const iconBgInactive =
  'text-sidebar-foreground/90 group-hover:bg-brand-accent/25 group-hover:text-sidebar-foreground';

export function StripItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const inner = (
    <span
      className={cn(
        'flex items-center justify-center w-8 h-8 rounded-(--sidebar-icon-radius) transition-colors',
        active ? iconBgActive : iconBgInactive,
      )}
    >
      {/* Bump the FA weight up (light → regular) so line icons read on
          the dark-teal rail. */}
      <i
        className={cn(
          item.icon.replace(/\bfa-(thin|light)\b/, 'fa-regular'),
          'text-base',
        )}
      />
    </span>
  );

  const cls = cn(
    'group flex justify-center w-full cursor-pointer',
    item.className,
  );

  if (item.onClick) {
    return (
      <Tooltip delay={150}>
        <TooltipTrigger
          render={
            <button
              type='button'
              onClick={() => {
                item.onClick?.();
                onNavigate?.();
              }}
              className={cls}
            />
          }
        >
          {inner}
        </TooltipTrigger>
        <TooltipContent side='bottom' sideOffset={8}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip delay={150}>
      <TooltipTrigger
        render={
          <Link to={item.href as never} onClick={onNavigate} className={cls} />
        }
      >
        {inner}
      </TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={8}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}
